import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams
} from '@angular/common/http';
import {
  Observable,
  of,
  forkJoin,
  throwError as observableThrowError
} from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
// import { HttpClientService } from '../../http/http-client.service';
import { environment } from 'apps/portal/src/environments/environment';
import { CMSContentService } from '../../../essentials/cms/content/cms-content.service';

export interface Token {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

@Injectable({
  providedIn: 'root'
})
export class AzureSearchService {
  private httpClient: HttpClient;

  public params: any;

  public host = environment.configAzure.endPointAPI;
  public version = environment.configAzure.versionAPI;
  public key = environment.configAzure.keyAPI;

  private highlights =
    'Name,Type,Category,SubCategory,Components/Title,Components/Subtitle,Components/Content';
  private selectFields = 'ID,Slug,Name,Type,Category,SubCategory,Components';

  constructor(
    httpClient: HttpClient,
    private contentService: CMSContentService
  ) {
    this.httpClient = httpClient;
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.message || 'Server Error');
  }

  public return(action: string, params?: any): Observable<any> {
    const url = this.host + action;

    let headers = new HttpHeaders();
    headers = headers.append('Access-Control-Allow-Origin', '*');
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('api-key', this.key);

    return this.httpClient
      .get(url, {
        headers,
        params
      })
      .pipe(tap(data => catchError(this.errorHandler)));
  }

  getCognitiveSearch(
    term,
    filter?: string,
    facet?: string,
    $top?: number,
    $skip?: number
  ) {
    if (filter && filter !== '') {
      this.params = {
        'api-version': this.version,
        search: term,
        highlight: this.highlights,
        $count: 'true',
        $filter: `Type eq '${filter}'`,
        $orderby: 'Data desc'
      };
    } else if ($top && $skip && facet) {
      this.params = {
        'api-version': this.version,
        search: term,
        highlight: this.highlights,
        $count: 'true',
        facet: facet ? facet : null,
        $top,
        $skip,
        $orderby: 'Data desc'
      };
    } else {
      this.params = {
        'api-version': this.version,
        search: term,
        highlight: this.highlights,
        $count: 'true',
        $orderby: 'Data desc'
      };
    }

    return this.return('', this.params).pipe(
      map(data => {
        return {
          count: data['@odata.count'],
          menu: data['@search.facets'],
          value: this.mapCognitiveSearch(data.value)
        };
      })
    );
  }

  getSuggestions(term, filter?: any) {
    let params: any = {
      search: term,
      suggesterName: 'sg',
      highlight: this.highlights,
      $count: 'true',
      $select: this.selectFields,
      'api-version': this.version,
      $orderby: 'Data desc'
    };

    if (filter) {
      params = {
        search: term,
        suggesterName: 'sg',
        $filter: `Type eq '${filter}'`,
        highlight: this.highlights,
        $count: 'true',
        $select: this.selectFields,
        'api-version': this.version,
        $orderby: 'Data desc'
      };
    }

    return this.return('/suggest', params).pipe(
      map(data => {
        return data.value.map(item => {
          return {
            type: item.Type,
            name: item.Name,
            url:
              '/' +
              this.removeAccents(item.Type).toLowerCase() +
              '/' +
              item.Slug
          };
        });
      })
    );
  }

  capitalize(value: string) {
    const string = value;
    if (string) {
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    }
    return '';
  }

  removeAccents(value: string) {
    const string = value;
    if (string) {
      return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    return '';
  }

  mapCognitiveSearch(results) {
    const data = results.map(item => {
      const newItem = {
        id: item.ID,
        slug: item.Slug,
        subcategory: item.SubCategory,
        name: item.Name,
        url: this.removeAccents(item.Type).toLowerCase() + '/' + item.Slug,
        type: item.Type,
        category: item.Category,
        subtitle: '',
        date: '',
        image: '',
        title: ''
      };

      const highlights = item['@search.highlights'];

      let highlight = '';

      if (highlights && highlights['Name']) {
        highlight = highlights['Name'][0].replace(
          /<(?!\/?em(?=>|\s.*>))\/?.*?>/g,
          ''
        );
        newItem.name = highlight;
        newItem.title =
          newItem.type +
          ' » ' +
          this.capitalize(newItem.category) +
          ' » ' +
          highlight;
      } else if (highlights && highlights['Components/Title']) {
        highlight = highlights['Components/Title'][0].replace(
          /<(?!\/?em(?=>|\s.*>))\/?.*?>/g,
          ''
        );
        newItem.name = highlight;
        newItem.title =
          newItem.type +
          ' » ' +
          this.capitalize(newItem.category) +
          ' » ' +
          highlight;
      } else {
        newItem.title =
          newItem.type +
          ' » ' +
          this.capitalize(newItem.category) +
          ' » ' +
          newItem.name;
      }

      if (highlights && highlights['Components/Subtitle']) {
        highlight = highlights['Components/Subtitle'][0].replace(
          /<(?!\/?em(?=>|\s.*>))\/?.*?>/g,
          ''
        );
        newItem.subtitle = highlight;
      } else if (highlights && highlights['Components/Content']) {
        highlight = highlights['Components/Content'][0].replace(
          /<(?!\/?em(?=>|\s.*>))\/?.*?>/g,
          ''
        );
        newItem.subtitle = highlight;
      } else {
        newItem.subtitle = item['Components'][0].Subtitle;
      }

      newItem.date = item['Components'][0].Data;
      newItem.image =
        environment.configCMS.endPointAssets +
        item['Components'][0].ImagemDestaque;

      return newItem;
    });
    return data;
  }
}
